// src/components/billing/UsageSummaryDisplay.jsx
import React from 'react';
import {
  Typography, Skeleton, Box, Select, MenuItem, FormControl, InputLabel, useTheme, Stack, Divider, alpha, Paper, Grid, Tooltip, Grow, Chip
} from '@mui/material';
import { 
    BarChartRounded as UsageIcon,
    DataUsageRounded as DataUsageIcon, // For empty state
    StorageRounded as TokensIcon,
    RequestQuoteRounded as CostIcon,
    SpeedRounded as InteractionsIcon,
    CreditScoreRounded as CreditsUsedIcon,
    InputRounded as PromptTokensIcon, // More specific
    OutputRounded as CompletionTokensIcon, // More specific
 } from '@mui/icons-material';
import SectionCard from './SectionCard'; // Ensure this path is correct

// Helper to format numbers, especially for credits which can have many decimals
const formatFlexibleDecimal = (value, isCurrency = false) => {
    if (value === null || value === undefined || isNaN(parseFloat(value))) return 'N/A';
    const num = parseFloat(value);
    if (isCurrency) return num.toFixed(4); // Provider cost usually needs more precision
    if (num === 0) return '0';
    if (Math.abs(num) < 0.000001) return num.toExponential(2); // Very small numbers
    if (Math.abs(num) < 1) return num.toFixed(6); // Small fractions
    if (num % 1 === 0) return num.toLocaleString(); // Whole numbers
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); // Typical decimals
};


const StatItem = ({ icon, label, value, unit = '', isCurrency = false, tooltip, highlight = false, fullWidthOnMd = false }) => {
    const theme = useTheme();
    const displayValue = formatFlexibleDecimal(value, isCurrency);
    const IconComponent = icon;

    return (
        <Grid item xs={12} sm={6} md={fullWidthOnMd ? 12 : 4} sx={{display:'flex'}}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: {xs:1.5, sm:2}, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: {xs:1.5, sm:2}, 
                    borderRadius: '16px', // More rounded stat items
                    border: `1px solid ${highlight ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                    background: highlight 
                        ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.light, 0.03)})` 
                        : alpha(theme.palette.background.default, 0.3),
                    flexGrow: 1, // Ensure it takes full width of its grid cell
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        borderColor: highlight ? theme.palette.primary.main : alpha(theme.palette.primary.light, 0.7),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.1)}`
                    }
                }}
            >
                <Box sx={{
                    width: {xs:38, sm:42}, height: {xs:38, sm:42}, borderRadius: '12px', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink:0,
                    bgcolor: highlight ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.text.secondary, 0.08),
                    color: highlight ? theme.palette.primary.main : theme.palette.text.secondary,
                }}>
                    <IconComponent sx={{fontSize: {xs:'1.3rem', sm:'1.5rem'}}} />
                </Box>
                <Box sx={{overflow:'hidden'}}>
                    <Tooltip title={tooltip || label} placement="top-start">
                        <Typography variant="body2" color="text.secondary" sx={{lineHeight:1.2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{label}</Typography>
                    </Tooltip>
                    <Typography 
                        variant={highlight ? "h4" : "h5"} // Emphasize highlighted stat
                        fontWeight={highlight ? 700 : 600} 
                        color={highlight ? theme.palette.primary.dark : "text.primary"}
                        sx={{lineHeight: 1.2, mt:0.25}}
                    >
                        {isCurrency && displayValue !== 'N/A' ? '$' : ''}
                        {displayValue}
                        {unit && displayValue !== 'N/A' && <Typography component="span" variant="caption" color="text.disabled" sx={{ml:0.5}}>{unit}</Typography>}
                    </Typography>
                </Box>
            </Paper>
        </Grid>
    );
};

const UsageSummaryDisplay = ({ summary, isLoading, onPeriodChange, selectedPeriod }) => {
  const theme = useTheme();
  const periodOptions = [
    { value: "today", label: "Today's Usage" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "current_month", label: "This Billing Cycle" },
    { value: "last_30_days", label: "Last 30 Days" },
    // { value: "previous_month", label: "Previous Month" }, // Could be added
  ];

  const headerAction = (
    <FormControl size="small" sx={{ minWidth: {xs:160, sm:200}, '.MuiOutlinedInput-root': {borderRadius:'12px'} }}>
      <InputLabel id="usage-period-select-label">Usage Period</InputLabel>
      <Select
        labelId="usage-period-select-label"
        label="Usage Period"
        value={selectedPeriod}
        onChange={(e) => onPeriodChange(e.target.value)}
        disabled={isLoading}
        variant="outlined"
        MenuProps={{ PaperProps: { sx: { borderRadius: '12px', mt:0.5, boxShadow: theme.shadows[4] } } }}
        sx={{ '.MuiSelect-select': { py: 1.25 } }}
      >
        {periodOptions.map(opt => (
          <MenuItem key={opt.value} value={opt.value} sx={{fontSize:'0.9rem', py:1.25, borderRadius:'8px', mx:1, my:0.5}}>{opt.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (isLoading) {
    return (
      <SectionCard title="Usage Insights" icon={<UsageIcon />} action={headerAction} fullHeight={false}>
        <Grid container spacing={2.5} sx={{mt:0.5}}>
            {[...Array(1)].map((_, i) => ( // Skeleton for main stat
                 <Grid item xs={12} key={`main-skel-${i}`}>
                    <Skeleton variant="rounded" height={90} sx={{borderRadius:'16px'}}/>
                </Grid>
            ))}
            {[...Array(3)].map((_, i) => ( // Skeletons for smaller stats
                <Grid item xs={12} sm={6} md={4} key={`sub-skel-${i}`}>
                    <Skeleton variant="rounded" height={90} sx={{borderRadius:'16px'}}/>
                </Grid>
            ))}
        </Grid>
      </SectionCard>
    );
  }

  // Handles both null summary (error fetching) and empty summary object (no data for period)
  if (!summary || Object.keys(summary).length === 0 || summary.total_credits_charged === undefined) {
    return (
      <SectionCard title="Usage Insights" icon={<UsageIcon />} action={headerAction} fullHeight={false}>
        <Stack alignItems="center" justifyContent="center" sx={{flexGrow:1, textAlign:'center', py:6, minHeight: 250}}>
            <DataUsageIcon sx={{fontSize: 64, color: 'text.disabled', mb:2.5, opacity:0.7}}/>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{fontWeight:500}}>No Usage Data Found</Typography>
            <Typography color="text.disabled" variant="body2">
                {(!summary || Object.keys(summary).length === 0) 
                    ? "Usage data might be unavailable or still processing for the selected period."
                    : "No service usage has been recorded for this period yet."}
            </Typography>
             {/* Optionally add a manual refresh button here too */}
        </Stack>
      </SectionCard>
    );
  }

  // API provides total_tokens, total_prompt_tokens, total_completion_tokens
  // The component was already set up to use these.

  return (
    <SectionCard title="Usage Insights" icon={<UsageIcon />} action={headerAction} fullHeight={false}>
        <Grow in={!isLoading} timeout={300}>
            <Grid container spacing={2.5} sx={{mt:0.5}}>
                <StatItem 
                    icon={CreditsUsedIcon} 
                    label="Total Credits Consumed" 
                    value={summary.total_credits_charged} 
                    unit="credits" 
                    highlight 
                    fullWidthOnMd // Makes this main stat take full width on medium screens up
                    tooltip={`Total credits deducted for services used: ${summary.period_label || selectedPeriod}.`}
                />
                <StatItem icon={InteractionsIcon} label="LLM Interactions" value={summary.interactions_count} unit="requests" tooltip="Number of successful calls to LLM services."/>
                <StatItem icon={TokensIcon} label="Total Tokens Processed" value={summary.total_tokens} unit="tokens" tooltip="Sum of all prompt and completion tokens." />
                <StatItem icon={CostIcon} label="Est. Provider Cost" value={summary.total_provider_cost_usd} unit="USD" isCurrency tooltip="Estimated cost from underlying LLM providers (e.g., OpenAI, Google)."/>
                
                { (summary.total_prompt_tokens !== undefined && summary.total_prompt_tokens > 0) || (summary.total_completion_tokens !== undefined && summary.total_completion_tokens > 0) ? (
                    <Grid item xs={12} sx={{my:1}}>
                        <Divider textAlign="left" sx={{
                             '&::before, &::after': { borderColor: alpha(theme.palette.primary.main, 0.3)},
                             color: theme.palette.primary.main, fontWeight:500, fontSize:'0.9rem',
                        }}>
                            Token Breakdown
                        </Divider>
                    </Grid>
                ) : null}

                {summary.total_prompt_tokens !== undefined && summary.total_prompt_tokens > 0 &&
                    <StatItem icon={PromptTokensIcon} label="Prompt Tokens" value={summary.total_prompt_tokens} unit="tokens" />
                }
                {summary.total_completion_tokens !== undefined && summary.total_completion_tokens > 0 &&
                    <StatItem icon={CompletionTokensIcon} label="Completion Tokens" value={summary.total_completion_tokens} unit="tokens" />
                }
            </Grid>
        </Grow>
    </SectionCard>
  );
};

export default UsageSummaryDisplay;